import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { t } from '../lib/i18n';
import { draftProposal } from '../sales/api';
import { messageFor } from '../lib/errors';
import { formatDate, formatDateTime, formatMoney } from '../ui/format';
import { Badge, ErrorState, Loading, Panel } from '../ui/Page';
import { Person } from '../ui/Avatar';
import { Menu } from '../ui/Menu';
import { MemberSelect, useMembers } from '../ui/UserSelect';
import { createTask } from '../work/api';
import { listCatalogue, listPipelines, type PipelineStage } from '../settings/catalogues-api';
import {
  BoxGlyph,
  CalendarGlyph,
  CheckGlyph,
  ClockGlyph,
  CrossGlyph,
  ExternalGlyph,
  FlagGlyph,
  MailGlyph,
  NoteGlyph,
} from './BoardIcons';
import { changesBetween, dueAt, formOf, textToCents, type DealForm } from './deal-form';
import {
  dealAttribution,
  getDeal,
  listDealItems,
  listProducts,
  moveDealStage,
  relatedTasks,
  saveDealItems,
  updateDeal,
  type DealItem,
  type DealView,
} from './detail-api';
import { searchEntity, type CompanyRow } from './api';

/**
 * One deal (context.md §35, §131).
 *
 * The screen is arranged around the order somebody reads it in. The heading answers "what is this
 * and is it still open"; the strip under it answers "how much, by when, whose"; the track answers
 * "where is it", and is the one control everybody uses every day. Only then come the fields, the
 * lines that make up the money, and the two small cards — what we know, and what happens next.
 *
 * Changes are saved from a button rather than on every keystroke. Somebody adjusting a price and a
 * close date together means one change, and three PATCHes for it is three chances for the middle
 * one to lose; the draft lives in `deal-form.ts`, which also decides what a typed amount is worth.
 * The exceptions are the stage and the notes, which are each a single decision with their own
 * control, so they save on the spot.
 *
 * Where the deal came from is read from the lead it was converted from and shown, never edited: it
 * is a fact about how somebody arrived, and a second editable copy would only be a second place for
 * it to be wrong.
 */

const TEMPERATURES = ['COLD', 'WARM', 'HOT', 'CLOSED'] as const;
const PRIORITIES = ['HIGH', 'MEDIUM', 'LOW'] as const;

export function DealPage(): React.JSX.Element {
  const { id = '' } = useParams();
  const deal = useQuery({ queryKey: ['deal', id], queryFn: () => getDeal(id), enabled: !!id });

  if (deal.isLoading) return <Loading />;
  if (deal.error) return <ErrorState error={deal.error} onRetry={() => void deal.refetch()} />;
  if (!deal.data) return <ErrorState error={new Error('not found')} />;

  // Keyed on the record, so a different deal starts with a clean draft rather than inheriting one.
  return <DealScreen key={deal.data.id} record={deal.data} />;
}

function DealScreen({ record }: { record: DealView }): React.JSX.Element {
  const id = record.id;
  const client = useQueryClient();
  const [tab, setTab] = useState<'items' | 'company' | 'history'>('items');
  const [error, setError] = useState<string | null>(null);
  const [scheduling, setScheduling] = useState(false);
  const [losing, setLosing] = useState(false);
  const [draft, setDraft] = useState<DealForm | null>(null);

  const pipelines = useQuery({ queryKey: ['pipelines'], queryFn: listPipelines });
  const attribution = useQuery({
    queryKey: ['deal-attribution', id],
    queryFn: () => dealAttribution(id),
  });
  const sources = useQuery({
    queryKey: ['catalogue', 'customer-sources'],
    queryFn: () => listCatalogue('customer-sources'),
  });
  const companies = useQuery({
    queryKey: ['companies', 'deal-names'],
    queryFn: () => searchEntity<CompanyRow>('companies', { limit: 200 }),
  });
  const tasks = useQuery({
    queryKey: ['deal-tasks', id],
    queryFn: () => relatedTasks('OPPORTUNITY', id),
  });

  const { options: members } = useMembers();

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['deal', id] });
    void client.invalidateQueries({ queryKey: ['board'] });
  };

  const save = useMutation({
    mutationFn: (changes: Record<string, unknown>) => updateDeal(id, changes),
    onSuccess: () => {
      setDraft(null);
      setError(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const move = useMutation({
    mutationFn: (input: { stageId: string; lostReason?: string }) =>
      moveDealStage(id, { ...input, expectedVersion: record.version }),
    onSuccess: () => {
      setError(null);
      setLosing(false);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const baseline = useMemo(() => formOf(record), [record]);
  const form = draft ?? baseline;
  const changes = changesBetween(baseline, form);
  const dirty = Object.keys(changes).length > 0;

  const edit = (patch: Partial<DealForm>): void => setDraft({ ...form, ...patch });

  const pipeline = pipelines.data?.pipelines.find((entry) => entry.id === record.pipeline_id);
  const stages = pipeline?.stages ?? [];
  const wonStage = stages.find((stage) => stage.is_won);
  const lostStage = stages.find((stage) => stage.is_lost);
  const company = companies.data?.results.find((entry) => entry.id === record.company_id);
  const owner = members.find((member) => member.id === form.ownerId);
  const source = (sources.data ?? []).find((entry) => entry.id === form.sourceId);
  const days = daysSince(record.stage_changed_at);

  return (
    <div className="view view--wide deal-page">
      <Link to="/funil" className="deal-page__back">
        ← {t('deal.backToBoard')}
      </Link>

      <header className="deal-head">
        <div className="deal-head__identity">
          <div className="deal-head__title">
            <h1>{record.title}</h1>
            <Badge
              tone={
                record.status === 'WON' ? 'positive' : record.status === 'LOST' ? 'danger' : 'info'
              }
            >
              {t(`deal.status.${record.status}`)}
            </Badge>
          </div>

          <p className="deal-head__meta">
            {company ? (
              <Link to={`/crm/registro/companies/${company.id}`}>{company.name}</Link>
            ) : (
              <span className="muted">{t('deal.noCompany')}</span>
            )}
            {company?.industry && <span className="muted">· {company.industry}</span>}
            {company?.email && (
              <span className="muted deal-head__mail">
                <MailGlyph />
                {company.email}
              </span>
            )}
          </p>
        </div>

        <div className="deal-head__actions">
          <button
            type="button"
            className="button button--quiet"
            onClick={() => setScheduling(true)}
          >
            <CalendarGlyph />
            {t('deal.schedule')}
          </button>

          <button
            type="button"
            className="button button--primary"
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate(changes)}
          >
            {save.isPending ? t('work.creating') : t('deal.saveChanges')}
          </button>

          <Menu label={t('deal.actions')}>
            <button type="button" onClick={() => setDraft(null)} disabled={!dirty}>
              {t('deal.discard')}
            </button>

            {company && (
              <Link to={`/crm/registro/companies/${company.id}`} role="menuitem">
                {t('deal.openCompany')}
              </Link>
            )}

            <Link to="/funil" role="menuitem">
              {t('deal.backToBoard')}
            </Link>
          </Menu>
        </div>
      </header>

      {/* The three figures somebody checks before they say anything on a call. */}
      <div className="deal-summary">
        <div className="deal-summary__cell">
          <span className="deal-summary__label">{t('deal.amountLabel')}</span>
          <strong className="deal-summary__value">{formatMoney(textToCents(form.amount))}</strong>
        </div>

        <div className="deal-summary__cell">
          <span className="deal-summary__label">{t('deal.expected')}</span>
          <span className="deal-summary__value deal-summary__value--inline">
            <CalendarGlyph />
            {form.expected ? formatDate(form.expected) : t('deal.noExpected')}
          </span>
        </div>

        <div className="deal-summary__cell">
          <span className="deal-summary__label">{t('deal.owner')}</span>
          {owner ? (
            <Person size="xs" name={owner.name} url={owner.avatarUrl} />
          ) : (
            <Person size="xs" name={t('deal.noOwner')} muted />
          )}
        </div>
      </div>

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <StageTrack
        stages={stages}
        currentId={record.stage_id}
        status={record.status}
        days={days}
        moving={move.isPending}
        wonStage={wonStage}
        lostStage={lostStage}
        onMove={(stageId) => move.mutate({ stageId })}
        onLose={() => setLosing(true)}
      />

      <div className="deal">
        <Panel title={t('deal.data')}>
          <div className="deal-fields">
            <label className="field">
              <span>{t('deal.value')}</span>
              <input
                value={form.amount}
                inputMode="decimal"
                onChange={(changeEvent) => edit({ amount: changeEvent.target.value })}
              />
            </label>

            <label className="field">
              <span>{t('deal.expected')}</span>
              <input
                type="date"
                value={form.expected}
                onChange={(changeEvent) => edit({ expected: changeEvent.target.value })}
              />
            </label>

            <label className="field">
              <span>{t('deal.priority')}</span>
              <span className="field-with-dot">
                <span
                  className="dot"
                  style={{ '--dot-color': priorityColor(form.priority) } as React.CSSProperties}
                  aria-hidden="true"
                />
                <select
                  className="select"
                  value={form.priority}
                  onChange={(changeEvent) =>
                    edit({ priority: changeEvent.target.value as DealForm['priority'] })
                  }
                >
                  {PRIORITIES.map((value) => (
                    <option key={value} value={value}>
                      {t(`deal.priority.${value}`)}
                    </option>
                  ))}
                </select>
              </span>
            </label>

            <label className="field">
              <span>{t('deal.temperature')}</span>
              <select
                className="select"
                value={form.temperature}
                onChange={(changeEvent) => edit({ temperature: changeEvent.target.value })}
              >
                <option value="">{t('deal.noTemperature')}</option>
                {TEMPERATURES.map((value) => (
                  <option key={value} value={value}>
                    {t(`deal.temperature.${value}`)}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span>{t('catalogues.tab.sources')}</span>
              <select
                className="select"
                value={form.sourceId}
                onChange={(changeEvent) => edit({ sourceId: changeEvent.target.value })}
              >
                <option value="">{t('deal.noSource')}</option>
                {(sources.data ?? []).map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.name}
                  </option>
                ))}
              </select>
            </label>

            <div className="field">
              <span>{t('deal.owner')}</span>
              <MemberSelect
                value={form.ownerId || null}
                placeholder={t('deal.noOwner')}
                onChange={(next) => edit({ ownerId: next ?? '' })}
              />
            </div>
          </div>

          <section className="deal-linked">
            <h3>{t('deal.linkedCompany')}</h3>

            {company ? (
              <Link to={`/crm/registro/companies/${company.id}`} className="deal-linked__card">
                <span className="cell-stack">
                  <span className="strong">{company.name}</span>
                  <span className="muted small">
                    {company.industry ?? source?.name ?? t('deal.noIndustry')}
                  </span>
                </span>
                <ExternalGlyph />
              </Link>
            ) : (
              <p className="muted small">{t('deal.noCompany')}</p>
            )}
          </section>

          {attribution.data && Object.values(attribution.data).some(Boolean) && (
            <section className="deal__origin">
              <h3>{t('deal.origin')}</h3>
              <dl className="summary">
                {attribution.data.landing_page && (
                  <div>
                    <dt>{t('deal.page')}</dt>
                    <dd className="small">{attribution.data.landing_page}</dd>
                  </div>
                )}
                {attribution.data.referrer && (
                  <div>
                    <dt>{t('deal.referrer')}</dt>
                    <dd className="small">{attribution.data.referrer}</dd>
                  </div>
                )}
                {attribution.data.ad && (
                  <div>
                    <dt>{t('deal.ad')}</dt>
                    <dd className="small">{attribution.data.ad}</dd>
                  </div>
                )}
                {attribution.data.campaign && (
                  <div>
                    <dt>{t('deal.campaign')}</dt>
                    <dd className="small">
                      {[attribution.data.source, attribution.data.medium, attribution.data.campaign]
                        .filter(Boolean)
                        .join(' · ')}
                    </dd>
                  </div>
                )}
              </dl>
            </section>
          )}
        </Panel>

        <div className="deal-detail">
          <div className="tabs" role="tablist">
            {(['items', 'company', 'history'] as const).map((key) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                className="tab"
                onClick={() => setTab(key)}
              >
                {t(`deal.tab.${key}`)}
              </button>
            ))}
          </div>

          {tab === 'items' && <ItemsTab dealId={id} />}

          {tab === 'company' && (
            <Panel>
              {company ? (
                <dl className="summary">
                  <div>
                    <dt>{t('crm.field.name')}</dt>
                    <dd>{company.name}</dd>
                  </div>
                  <div>
                    <dt>{t('crm.field.industry')}</dt>
                    <dd>{company.industry ?? '—'}</dd>
                  </div>
                  <div>
                    <dt>E-mail</dt>
                    <dd>{company.email ?? '—'}</dd>
                  </div>
                </dl>
              ) : (
                <p className="muted small">{t('deal.noCompany')}</p>
              )}
            </Panel>
          )}

          {tab === 'history' && (
            <Panel>
              <dl className="summary">
                <div>
                  <dt>{t('deal.createdAt')}</dt>
                  <dd className="small">{formatDateTime(record.created_at)}</dd>
                </div>
                <div>
                  <dt>{t('deal.movedAt')}</dt>
                  <dd className="small">{formatDateTime(record.stage_changed_at)}</dd>
                </div>
                {record.expected_close_date && (
                  <div>
                    <dt>{t('deal.expected')}</dt>
                    <dd className="small">{formatDate(record.expected_close_date)}</dd>
                  </div>
                )}
              </dl>

              <p className="muted small">{t('deal.historyHint')}</p>
            </Panel>
          )}
        </div>
      </div>

      <div className="deal-cards">
        <NotesCard
          notes={record.notes}
          saving={save.isPending}
          onSave={(notes) => save.mutate({ notes })}
        />

        <NextActionCard tasks={tasks.data?.tasks ?? []} onSchedule={() => setScheduling(true)} />
      </div>

      {losing && lostStage && (
        <LostDialog
          saving={move.isPending}
          onClose={() => setLosing(false)}
          onConfirm={(reason) => move.mutate({ stageId: lostStage.id, lostReason: reason })}
        />
      )}

      {scheduling && (
        <ScheduleDialog
          dealId={id}
          title={record.title}
          ownerId={form.ownerId || null}
          onDone={() => {
            setScheduling(false);
            void client.invalidateQueries({ queryKey: ['deal-tasks', id] });
          }}
          onClose={() => setScheduling(false)}
        />
      )}
    </div>
  );
}

/**
 * Where the deal is, as something to click rather than something to read.
 *
 * The open stages are chevrons, because a funnel has a direction and a row of equal boxes does not
 * say so. Won and lost sit apart from them: they are outcomes, not another step, and a stage
 * somebody can reach by accident while scanning left to right is a deal marked lost by accident.
 */
function StageTrack({
  stages,
  currentId,
  status,
  days,
  moving,
  wonStage,
  lostStage,
  onMove,
  onLose,
}: {
  stages: readonly PipelineStage[];
  currentId: string;
  status: DealView['status'];
  days: number;
  moving: boolean;
  wonStage: PipelineStage | undefined;
  lostStage: PipelineStage | undefined;
  onMove: (stageId: string) => void;
  /** Losing is not a move: the server refuses one without a reason, so the screen asks for it. */
  onLose: () => void;
}): React.JSX.Element {
  const open = stages.filter((stage) => !stage.is_won && !stage.is_lost);
  const reached = open.findIndex((stage) => stage.id === currentId);

  return (
    <section className="panel track">
      <div className="panel__header track__head">
        <h2>{t('deal.stagePanel')}</h2>

        <div className="track__actions">
          <span className="muted small cell-inline">
            <ClockGlyph />
            {t('deal.inStage').replace('{n}', String(days))}
          </span>

          <button
            type="button"
            className={`button button--won${status === 'WON' ? ' is-active' : ''}`}
            disabled={!wonStage || moving || status === 'WON'}
            onClick={() => wonStage && onMove(wonStage.id)}
          >
            <CheckGlyph />
            {t('deal.status.WON')}
          </button>

          <button
            type="button"
            className={`button button--lost${status === 'LOST' ? ' is-active' : ''}`}
            disabled={!lostStage || moving || status === 'LOST'}
            onClick={onLose}
          >
            <CrossGlyph />
            {t('deal.status.LOST')}
          </button>
        </div>
      </div>

      <div className="panel__body">
        <ol className="track__steps">
          {open.map((stage, index) => {
            const current = stage.id === currentId;

            return (
              <li key={stage.id}>
                <button
                  type="button"
                  className={[
                    'track__step',
                    current ? 'track__step--current' : '',
                    reached >= 0 && index < reached ? 'track__step--done' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  aria-current={current ? 'step' : undefined}
                  disabled={current || moving}
                  title={t('board.stageProbability').replace('{n}', String(stage.probability))}
                  onClick={() => onMove(stage.id)}
                >
                  <span className="track__name">{stage.name}</span>
                  {current && <span className="track__now">{t('deal.current')}</span>}
                </button>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}

/**
 * What the deal is for.
 *
 * Edited as a set and saved once, because the total is read while it is being edited and a total
 * that changes line by line while somebody is typing is worse than one that changes when they are
 * done.
 */
function ItemsTab({ dealId }: { dealId: string }): React.JSX.Element {
  const client = useQueryClient();
  const navigate = useNavigate();
  const [draft, setDraft] = useState<DealItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const propose = useMutation({
    mutationFn: () => draftProposal({ opportunityId: dealId }),
    onSuccess: (proposal) => navigate(`/vendas/propostas/${proposal.id}`),
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const items = useQuery({
    queryKey: ['deal-items', dealId],
    queryFn: () => listDealItems(dealId),
  });
  const products = useQuery({ queryKey: ['products'], queryFn: listProducts });

  const save = useMutation({
    mutationFn: () =>
      saveDealItems(
        dealId,
        (draft ?? []).map((item) => ({
          productId: item.product_id,
          description: item.description,
          quantity: item.quantity,
          unitPriceCents: item.unit_price_cents,
          discountBps: item.discount_bps,
        })),
      ),
    onSuccess: () => {
      setDraft(null);
      setError(null);
      void client.invalidateQueries({ queryKey: ['deal-items', dealId] });
      void client.invalidateQueries({ queryKey: ['deal', dealId] });
      void client.invalidateQueries({ queryKey: ['board'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  if (items.isLoading) return <Loading />;

  const lines = draft ?? items.data?.items ?? [];
  const total = lines.reduce((sum, line) => sum + lineTotal(line), 0);

  const edit = (index: number, changes: Partial<DealItem>): void => {
    setDraft(lines.map((line, position) => (position === index ? { ...line, ...changes } : line)));
  };

  return (
    <Panel>
      {lines.length === 0 ? (
        <div className="deal-items__empty">
          <BoxGlyph />
          <p className="strong">{t('deal.noItems')}</p>
          <p className="muted small">{t('deal.noItemsBody')}</p>
        </div>
      ) : (
        <div className="table-scroll">
          <table className="table deal-items">
            <thead>
              <tr>
                <th>{t('deal.item')}</th>
                <th>{t('deal.quantity')}</th>
                <th>{t('deal.unitPrice')}</th>
                <th>{t('deal.discount')}</th>
                <th className="align-end">{t('deal.subtotal')}</th>
                <th aria-label={t('catalogues.remove')} />
              </tr>
            </thead>

            <tbody>
              {lines.map((line, index) => (
                <tr key={line.id || `new-${index}`}>
                  <td>
                    <input
                      className="cell-input"
                      value={line.description}
                      aria-label={t('deal.item')}
                      onChange={(changeEvent) =>
                        edit(index, { description: changeEvent.target.value })
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="cell-input cell-input--narrow"
                      type="number"
                      min={0.001}
                      step={0.5}
                      value={line.quantity}
                      aria-label={t('deal.quantity')}
                      onChange={(changeEvent) =>
                        edit(index, { quantity: Number(changeEvent.target.value) })
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="cell-input cell-input--narrow"
                      type="number"
                      min={0}
                      step={1}
                      value={line.unit_price_cents / 100}
                      aria-label={t('deal.unitPrice')}
                      onChange={(changeEvent) =>
                        edit(index, {
                          unit_price_cents: Math.round(Number(changeEvent.target.value) * 100),
                        })
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="cell-input cell-input--narrow"
                      type="number"
                      min={0}
                      max={100}
                      value={line.discount_bps / 100}
                      aria-label={t('deal.discount')}
                      onChange={(changeEvent) =>
                        edit(index, {
                          discount_bps: Math.round(Number(changeEvent.target.value) * 100),
                        })
                      }
                    />
                  </td>
                  <td className="align-end strong">{formatMoney(lineTotal(line))}</td>
                  <td className="align-end">
                    <button
                      type="button"
                      className="button button--quiet button--compact"
                      onClick={() => setDraft(lines.filter((_, position) => position !== index))}
                    >
                      {t('catalogues.remove')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <div className="deal-items__foot">
        <select
          className="select"
          value=""
          aria-label={t('deal.addItem')}
          onChange={(changeEvent) => {
            const product = products.data?.products.find(
              (candidate) => candidate.id === changeEvent.target.value,
            );

            setDraft([
              ...lines,
              {
                id: '',
                product_id: product?.id ?? null,
                description: product?.name ?? '',
                quantity: 1,
                unit_price_cents: product?.unit_price_cents ?? 0,
                discount_bps: 0,
                total_cents: 0,
              },
            ]);
          }}
        >
          <option value="">+ {t('deal.addItem')}</option>
          {(products.data?.products ?? []).map((product) => (
            <option key={product.id} value={product.id}>
              {product.name} — {formatMoney(product.unit_price_cents)}
            </option>
          ))}
        </select>

        <span className="deal-items__total">
          <span className="muted small">{t('deal.itemsTotal')}</span>
          <strong>{formatMoney(total)}</strong>
        </span>

        {draft !== null && (
          <button
            type="button"
            className="button button--primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? t('work.creating') : t('deal.saveItems')}
          </button>
        )}

        {/* From the lines as they stand: a proposal is a snapshot of exactly this, so it waits
            until the draft has been saved rather than quoting something nobody agreed to. */}
        <button
          type="button"
          className="button button--quiet"
          disabled={propose.isPending || lines.length === 0 || draft !== null}
          onClick={() => propose.mutate()}
        >
          {t('deal.propose')}
        </button>
      </div>
    </Panel>
  );
}

/**
 * What somebody wrote down.
 *
 * Read-only until "Editar", because these notes are read far more often than they are written and
 * a textarea sitting open is an invitation to change one by leaning on the keyboard.
 */
function NotesCard({
  notes,
  saving,
  onSave,
}: {
  notes: string | null;
  saving: boolean;
  onSave: (notes: string | null) => void;
}): React.JSX.Element {
  const [text, setText] = useState<string | null>(null);
  const editing = text !== null;

  return (
    <Panel
      title={t('deal.internalNotes')}
      actions={
        editing ? (
          <span className="cell-inline">
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={() => setText(null)}
            >
              {t('confirm.cancel')}
            </button>
            <button
              type="button"
              className="button button--primary button--compact"
              disabled={saving}
              onClick={() => {
                onSave(text.trim() || null);
                setText(null);
              }}
            >
              {t('deal.save')}
            </button>
          </span>
        ) : (
          <button
            type="button"
            className="button button--quiet button--compact"
            onClick={() => setText(notes ?? '')}
          >
            <NoteGlyph />
            {t('deal.edit')}
          </button>
        )
      }
    >
      {editing ? (
        <textarea
          rows={4}
          value={text}
          autoFocus
          aria-label={t('deal.internalNotes')}
          onChange={(changeEvent) => setText(changeEvent.target.value)}
        />
      ) : notes ? (
        <p className="deal-notes">{notes}</p>
      ) : (
        <p className="muted small">{t('deal.notesEmpty')}</p>
      )}
    </Panel>
  );
}

/** What happens next, or the fact that nobody has decided yet. */
function NextActionCard({
  tasks,
  onSchedule,
}: {
  tasks: readonly { id: string; title: string; status: string; due_date: string | null }[];
  onSchedule: () => void;
}): React.JSX.Element {
  const open = tasks
    .filter((task) => task.status !== 'DONE' && task.status !== 'CANCELED')
    .sort((left, right) => (left.due_date ?? '9999').localeCompare(right.due_date ?? '9999'));

  return (
    <Panel title={t('deal.nextAction')}>
      {open.length === 0 ? (
        <div className="deal-next__empty">
          <FlagGlyph />
          <p className="strong">{t('deal.nextActionEmpty')}</p>
          <p className="muted small">{t('deal.nextActionHint')}</p>

          <button
            type="button"
            className="button button--quiet button--compact"
            onClick={onSchedule}
          >
            + {t('deal.schedule')}
          </button>
        </div>
      ) : (
        <ul className="deal-next">
          {open.slice(0, 4).map((task) => (
            <li key={task.id}>
              <Link to={`/trabalho/tarefas/${task.id}`} className="cell-stack">
                <span className="strong">{task.title}</span>
                <span className="muted small">
                  {task.due_date ? formatDate(task.due_date) : t('deal.noExpected')} ·{' '}
                  {t(`work.status.${task.status}`)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/**
 * Promising to do something about the deal.
 *
 * A task rather than a calendar event: most of what somebody schedules from here is "ligar na
 * terça", which has a day and an owner and no hour. It is created linked to the deal, so the card
 * above finds it — a follow-up loose in a task list is one nobody working the deal will see.
 *
 * A real `<dialog showModal>`, so the browser owns the backdrop, the focus trap and Escape.
 */
function ScheduleDialog({
  dealId,
  title,
  ownerId,
  onDone,
  onClose,
}: {
  dealId: string;
  title: string;
  ownerId: string | null;
  onDone: () => void;
  onClose: () => void;
}): React.JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null);
  const [what, setWhat] = useState('');
  const [when, setWhen] = useState(() =>
    new Date(Date.now() + 86_400_000).toISOString().slice(0, 10),
  );
  const [assignee, setAssignee] = useState<string | null>(ownerId);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const element = dialog.current;

    if (element && !element.open) element.showModal();
  }, []);

  const create = useMutation({
    mutationFn: () =>
      createTask({
        title: what.trim(),
        dueDate: when ? dueAt(when) : null,
        assigneeId: assignee,
        related: { type: 'OPPORTUNITY', id: dealId },
      }),
    onSuccess: onDone,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  return (
    <dialog
      ref={dialog}
      className="confirm"
      aria-label={t('deal.scheduleTitle')}
      onCancel={(cancelEvent) => {
        cancelEvent.preventDefault();
        onClose();
      }}
      onClose={onClose}
      onClick={(clickEvent) => {
        if (clickEvent.target === dialog.current) onClose();
      }}
    >
      <form
        onSubmit={(submitEvent) => {
          submitEvent.preventDefault();

          if (what.trim()) create.mutate();
        }}
      >
        <h2>{t('deal.scheduleTitle')}</h2>
        <p className="muted small">{title}</p>

        <label className="field">
          <span>{t('deal.scheduleWhat')}</span>
          <input
            value={what}
            autoFocus
            maxLength={300}
            placeholder={t('deal.schedulePlaceholder')}
            onChange={(changeEvent) => setWhat(changeEvent.target.value)}
          />
        </label>

        <label className="field">
          <span>{t('deal.scheduleWhen')}</span>
          <input
            type="date"
            value={when}
            onChange={(changeEvent) => setWhen(changeEvent.target.value)}
          />
        </label>

        <div className="field">
          <span>{t('deal.owner')}</span>
          <MemberSelect
            value={assignee}
            placeholder={t('deal.noOwner')}
            onChange={(next) => setAssignee(next)}
          />
        </div>

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <div className="confirm__actions">
          <button type="button" className="button button--quiet" onClick={onClose}>
            {t('confirm.cancel')}
          </button>
          <button
            type="submit"
            className="button button--primary"
            disabled={!what.trim() || create.isPending}
          >
            {create.isPending ? t('work.creating') : t('deal.scheduleCreate')}
          </button>
        </div>
      </form>
    </dialog>
  );
}

/**
 * Why the deal was lost.
 *
 * The server refuses to close a deal as lost without a reason, and it is right to: "why did we lose
 * this" is the whole point of tracking losses, and a field somebody can skip is a field that is
 * empty on exactly the deals worth learning from. So the button asks rather than failing.
 *
 * The tenant's own catalogue first, with a free line underneath for the one that is not on it —
 * offering only free text produces forty spellings of "preço" and no report.
 */
function LostDialog({
  saving,
  onConfirm,
  onClose,
}: {
  saving: boolean;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}): React.JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null);
  const [chosen, setChosen] = useState('');
  const [other, setOther] = useState('');

  const reasons = useQuery({
    queryKey: ['catalogue', 'loss-reasons'],
    queryFn: () => listCatalogue('loss-reasons'),
  });

  useEffect(() => {
    const element = dialog.current;

    if (element && !element.open) element.showModal();
  }, []);

  const reason = (chosen === 'OTHER' ? other : chosen).trim();

  return (
    <dialog
      ref={dialog}
      className="confirm"
      aria-label={t('deal.lostTitle')}
      onCancel={(cancelEvent) => {
        cancelEvent.preventDefault();
        onClose();
      }}
      onClose={onClose}
      onClick={(clickEvent) => {
        if (clickEvent.target === dialog.current) onClose();
      }}
    >
      <form
        onSubmit={(submitEvent) => {
          submitEvent.preventDefault();

          if (reason) onConfirm(reason);
        }}
      >
        <h2>{t('deal.lostTitle')}</h2>
        <p className="muted small">{t('deal.lostHint')}</p>

        <label className="field">
          <span>{t('deal.lostReason')}</span>
          <select
            className="select"
            value={chosen}
            onChange={(changeEvent) => setChosen(changeEvent.target.value)}
          >
            <option value="">{t('deal.lostPick')}</option>
            {(reasons.data ?? []).map((entry) => (
              <option key={entry.id} value={entry.name}>
                {entry.name}
              </option>
            ))}
            <option value="OTHER">{t('deal.lostOther')}</option>
          </select>
        </label>

        {chosen === 'OTHER' && (
          <label className="field">
            <span>{t('deal.lostOtherLabel')}</span>
            <input
              value={other}
              autoFocus
              maxLength={500}
              onChange={(changeEvent) => setOther(changeEvent.target.value)}
            />
          </label>
        )}

        <div className="confirm__actions">
          <button type="button" className="button button--quiet" onClick={onClose}>
            {t('confirm.cancel')}
          </button>
          <button type="submit" className="button button--lost" disabled={!reason || saving}>
            <CrossGlyph />
            {t('deal.lostConfirm')}
          </button>
        </div>
      </form>
    </dialog>
  );
}

/** One line, after its discount. Basis points, so a third off is exact rather than 33%. */
function lineTotal(line: DealItem): number {
  return Math.round(
    (line.quantity * line.unit_price_cents * (10_000 - line.discount_bps)) / 10_000,
  );
}

/** Whole days, rounded down: "0 dias" is today, which is what somebody means by it. */
function daysSince(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
}

/** The dot beside the priority. Red is urgent, amber is next, grey is whenever. */
function priorityColor(priority: DealForm['priority']): string {
  if (priority === 'HIGH') return 'var(--color-down)';
  if (priority === 'MEDIUM') return 'var(--color-pending)';

  return 'var(--color-muted)';
}
